import React, { ChangeEvent } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
    setAnswerGenerationPromptTemplate,
    setAssistantType,
    setCitationsHighlightMode,
    setCognitiveSearchQueryGenerationPromptTemplate,
    setDocsSearchThreshold,
    setImproveSearchQueryMode,
    setModel,
    setTokensUsageMode,
    setModelProvider,
    setPdfCitationDisplayMode,
    setSearchApproach,
    setShowWelcomeMessage,
    setTemperatureCount,
    setUseSuggestFollowupQuestions,
    setPreselectContexFilesMode,
    setDocsPreselectSearchThreshold,
    setUserType,
    setUseReasoning,
} from "@/slices/chatSlice"
import { useLocalStorage } from "usehooks-ts"
import { DEFAULT_USE_REASONING } from "@/utils/brand"
import { QualityMode, overridesToQualityMode, qualityModeToOverrides } from "@/components/Settings/qualityMode"
import {
    AssistantTypes,
    PreselectContextFilesModes,
    ImproveSearchQueryModes,
    ModelVersions,
    SearchApproaches,
    TokensUsageModes,
    UserTypes,
} from "@/api"
import { SelectChangeEvent, Button, Box } from "@mui/material"
import { DownloadType } from "@/components/DownloadFromBlob/downloadFromBlob"
import { CitationsHighlightMode, DEFAULT_CITATIONS_HIGHLIGHT_MODE } from "@/components/CitationPDFViewer/citationsHighlightMode"
import { t } from "i18next"

interface Option {
    text: string
    key:
        | SearchApproaches
        | ModelVersions
        | AssistantTypes
        | ImproveSearchQueryModes
        | PreselectContextFilesModes
        | TokensUsageModes
        | UserTypes
}

interface PersonalSettings {
    userType: UserTypes
    assistantType: AssistantTypes
    selectedModel: ModelVersions
    selectedTokensUsageMode: TokensUsageModes
    qualityMode: QualityMode
    improveSearchQueryMode: ImproveSearchQueryModes
    answerGenerationPromptTemplate: string
    cognitiveSearchQueryGenerationPromptTemplate: string
    temperatureCount: number
    docsSearchThreshold: number
    useSuggestFollowupQuestions: boolean
    preselectContextFilesMode: PreselectContextFilesModes
    searchApproach: SearchApproaches
    showWelcomeMessage: boolean
    pdfCitationDisplayMode: DownloadType
    citationsHighlightMode: CitationsHighlightMode
    modelProvider: string
    docsPreselectSearchThreshold: number
    useReasoning: boolean
}

export const getSearchApproachOptions = (): Option[] => [
    {
        text: t("Auto (the type of search is automatically determined, suitable for most cases)"),
        key: SearchApproaches.Auto,
    },
    {
        text: t("Text (combines semantic understanding with exact keyword matching for the most relevant results)"),
        key: SearchApproaches.Text,
    },
    {
        text: t(
            "Aggregator (use when you want to summarize/collect/compare/resume ALL information from the documents at the same time)"
        ),
        key: SearchApproaches.Aggregator,
    },
]

export const getModelOptions = (): Option[] => [
    { text: t("Pro (Default)"), key: ModelVersions.PRO },
]

export const getTokensUsageOptions = (): Option[] => [
    { text: t("Optimal Efficiency (balanced performance and cost)"), key: TokensUsageModes.Standard },
    { text: t("High Performance (enhanced quality with moderate cost)"), key: TokensUsageModes.Increased },
    {
        text: t("Superior Quality (maximum quality at higher cost and slower speed)"),
        key: TokensUsageModes.Highest,
    },
]

export const getImproveSearchQueryModesOptions = (): Option[] => [
    { text: t("Always (your query will always be improved)"), key: ImproveSearchQueryModes.Always },
    {
        text: t("Manual (you can manually choose if you want your query to be improved)"),
        key: ImproveSearchQueryModes.Manual,
    },
    { text: t("Disabled (your query will not be improved)"), key: ImproveSearchQueryModes.Disabled },
]

export const getAutomaticallyPreselectContextFilesOptions = (): Option[] => [
    {
        text: t("Enabled (documents will be preselected automatically if none are already chosen)"),
        key: PreselectContextFilesModes.Enabled,
    },
    { text: t("Disabled"), key: PreselectContextFilesModes.Disabled },
]

const enableInternetSearch = import.meta.env.VITE_ENABLE_INTERNET_SEARCH !== "false"

export const getAssistantTypeOptions = (): Option[] => [
    { text: t("Knowledge Assistant"), key: AssistantTypes.DOC_ASSISTANT },
    ...(enableInternetSearch ? [{ text: t("Internet Search Assistant"), key: AssistantTypes.INTERNET_SEARCH }] : []),
]

export const getUserTypeOptions = (): Option[] => [
    { text: t("Beginner"), key: UserTypes.Beginner },
    { text: t("Expert"), key: UserTypes.Expert },
]

export type ProviderTypes = "azure" | "openai"

interface ProviderOption {
    text: string
    key: ProviderTypes
}

export const getProviderOptions = (): ProviderOption[] => [
    { text: t("Azure"), key: "azure" },
    { text: t("OpenAI"), key: "openai" },
]

export const DEFAULT_PERSONAL_SETTINGS: PersonalSettings = {
    answerGenerationPromptTemplate: "",
    cognitiveSearchQueryGenerationPromptTemplate: "",
    useSuggestFollowupQuestions: false,
    preselectContextFilesMode: PreselectContextFilesModes.Enabled,
    searchApproach: SearchApproaches.Auto,
    temperatureCount: 0.3,
    docsPreselectSearchThreshold: 50,
    docsSearchThreshold: 30,
    selectedModel: ModelVersions.PRO,
    assistantType: AssistantTypes.DOC_ASSISTANT,
    userType: UserTypes.Expert,
    pdfCitationDisplayMode: DownloadType.Auto,
    citationsHighlightMode: DEFAULT_CITATIONS_HIGHLIGHT_MODE,
    modelProvider: "azure",
    improveSearchQueryMode: ImproveSearchQueryModes.Manual,
    selectedTokensUsageMode: TokensUsageModes.Increased,
    qualityMode: QualityMode.Balanced,
    useReasoning: DEFAULT_USE_REASONING,
    showWelcomeMessage: false,
}

export function migratePersonalSettings(stored: Partial<PersonalSettings> | null | undefined): PersonalSettings {
    const sanitized = sanitizePersonalSettings(stored)
    const merged = { ...DEFAULT_PERSONAL_SETTINGS, ...sanitized, selectedModel: ModelVersions.PRO }
    if (sanitized.qualityMode === undefined) {
        merged.qualityMode = overridesToQualityMode(merged.selectedTokensUsageMode, merged.useReasoning)
    }
    return merged
}

function sanitizePersonalSettings(stored: Partial<PersonalSettings> | null | undefined): Partial<PersonalSettings> {
    if (!stored) return {}
    const knownKeys = new Set(Object.keys(DEFAULT_PERSONAL_SETTINGS))
    return Object.fromEntries(Object.entries(stored).filter(([key]) => knownKeys.has(key))) as Partial<PersonalSettings>
}

export const ResetSettingsButton = () => {
    const dispatch = useDispatch()
    const [, setPersonalSettings] = useLocalStorage("personalSettings", DEFAULT_PERSONAL_SETTINGS)

    const resetSettings = () => {
        const defaultSettings = DEFAULT_PERSONAL_SETTINGS

        Object.entries(defaultSettings).forEach(([key, value]) => {
            const actionCreator = {
                answerGenerationPromptTemplate: setAnswerGenerationPromptTemplate,
                cognitiveSearchQueryGenerationPromptTemplate: setCognitiveSearchQueryGenerationPromptTemplate,
                useSuggestFollowupQuestions: setUseSuggestFollowupQuestions,
                preselectContextFilesMode: setPreselectContexFilesMode,
                searchApproach: setSearchApproach,
                temperatureCount: setTemperatureCount,
                docsPreselectSearchThreshold: setDocsPreselectSearchThreshold,
                docsSearchThreshold: setDocsSearchThreshold,
                selectedModel: setModel,
                selectedTokensUsageMode: setTokensUsageMode,
                assistantType: setAssistantType,
                userType: setUserType,
                pdfCitationDisplayMode: setPdfCitationDisplayMode,
                citationsHighlightMode: setCitationsHighlightMode,
                modelProvider: setModelProvider,
                improveSearchQueryMode: setImproveSearchQueryMode,
                useReasoning: setUseReasoning,
            }[key]

            if (actionCreator) {
                dispatch(actionCreator(value))
            }
        })

        setPersonalSettings(defaultSettings)
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: "20px",
            }}>
            <Button variant="outlined" color="primary" onClick={resetSettings} sx={{ marginRight: "10px" }}>
                {t("Reset all settings to their default values")}
            </Button>
        </Box>
    )
}
export const useSettings = () => {
    const dispatch = useDispatch()
    const settings = useSelector((state: any) => state.chat)
    const [rawStoredSettings, setPersonalSettings] = useLocalStorage<Partial<PersonalSettings>>(
        "personalSettings",
        DEFAULT_PERSONAL_SETTINGS
    )
    const personalSettings = migratePersonalSettings(rawStoredSettings)

    const persistSettings = (updates: Partial<PersonalSettings>) => {
        setPersonalSettings(migratePersonalSettings({ ...personalSettings, ...updates }))
    }

    const selectedAssistantTypeState = Object.values(getAssistantTypeOptions()).find(
        i => i.key === settings.assistantType
    )

    const selectedUserTypeState = Object.values(getUserTypeOptions()).find(i => i.key === settings.userType)

    const selectedModelState = Object.values(getModelOptions()).find(i => i.key === settings.model)
    const selectedTokensUsageModeState = Object.values(getTokensUsageOptions()).find(
        i => i.key === settings.selectedTokensUsageMode
    )

    const selectedImproveSearchQueryModeState = Object.values(getImproveSearchQueryModesOptions()).find(
        i => i.key === settings.improveSearchQueryMode
    )

    const selectedSearchApproachState = Object.values(getSearchApproachOptions()).find(
        i => i.key === settings.searchApproach
    )

    const selectedPreselectContextFilesModeState = Object.values(getAutomaticallyPreselectContextFilesOptions()).find(
        i => i.key === settings.preselectContextFilesMode
    )

    const selectedProviderState = Object.values(getProviderOptions()).find(i => i.key === settings.modelProvider)

    const selectedDocsPreselectSearchThreshold = settings.docsPreselectSearchThreshold

    const onAnswerGenerationPromptTemplateChange = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(setAnswerGenerationPromptTemplate(event.target.value || ""))
        persistSettings({ answerGenerationPromptTemplate: event.target.value })
    }

    const onCognitiveSearchQueryGenerationPromptTemplateChange = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(setCognitiveSearchQueryGenerationPromptTemplate(event.target.value || ""))
        persistSettings({ cognitiveSearchQueryGenerationPromptTemplate: event.target.value })
    }

    const onTemperatureCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = parseFloat(event.target.value)

        if (newValue >= 0 && newValue <= 1) {
            dispatch(setTemperatureCount(newValue))
            persistSettings({ temperatureCount: newValue })
        }
    }

    const onDocsPreselectSearchThresholdChange = (
        event: ChangeEvent<HTMLInputElement> | number,
        newValue?: number | number[]
    ) => {
        let updatedValue: number
        if (typeof event === "number") {
            updatedValue = event
        } else if (newValue !== undefined) {
            updatedValue = newValue as number
        } else {
            updatedValue = parseInt((event.target as HTMLInputElement).value)
        }

        if (updatedValue >= 0 && updatedValue <= 100) {
            dispatch(setDocsPreselectSearchThreshold(updatedValue))
            persistSettings({ docsPreselectSearchThreshold: updatedValue })
        }
    }

    const onDocsSearchThresholdChange = (
        event: ChangeEvent<HTMLInputElement> | number,
        newValue?: number | number[]
    ) => {
        let updatedValue: number
        if (typeof event === "number") {
            updatedValue = event
        } else if (newValue !== undefined) {
            updatedValue = newValue as number
        } else {
            updatedValue = parseInt((event.target as HTMLInputElement).value)
        }

        if (updatedValue >= 0 && updatedValue <= 100) {
            dispatch(setDocsSearchThreshold(updatedValue))
            persistSettings({ docsSearchThreshold: updatedValue })
        }
    }

    const onUseSuggestFollowupQuestionsChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        checked?: boolean
    ) => {
        dispatch(setUseSuggestFollowupQuestions(!!checked))
        persistSettings({ useSuggestFollowupQuestions: !!checked })
    }

    const onPreselectContextFilesModeChange = (event: SelectChangeEvent) => {
        const selectedMode = JSON.parse(event.target.value)
        dispatch(setPreselectContexFilesMode(selectedMode.key))
        persistSettings({ preselectContextFilesMode: selectedMode?.key })
    }

    const onAssistantTypeChange = (event: SelectChangeEvent) => {
        const assistantType = JSON.parse(event.target.value)
        dispatch(setAssistantType(assistantType.key))
        persistSettings({ assistantType: assistantType?.key })
    }

    const onUserTypeChange = (event: SelectChangeEvent) => {
        const userType = JSON.parse(event.target.value)
        dispatch(setUserType(userType.key))
        persistSettings({ userType: userType?.key })
    }

    const onModelChange = (event: SelectChangeEvent) => {
        const selectedModel = JSON.parse(event.target.value)
        dispatch(setModel(selectedModel.key))
        persistSettings({ selectedModel: selectedModel?.key })
    }

    const onTokensUsageModeChange = (event: SelectChangeEvent) => {
        const selectedTokensUsageMode = JSON.parse(event.target.value)
        dispatch(setTokensUsageMode(selectedTokensUsageMode.key))
        const useReasoning = settings.useReasoning
        persistSettings({
            selectedTokensUsageMode: selectedTokensUsageMode?.key,
            qualityMode: overridesToQualityMode(selectedTokensUsageMode?.key, useReasoning),
        })
    }

    const onImproveSearchQueryModeChange = (event: SelectChangeEvent) => {
        const selectedMode = JSON.parse(event.target.value)
        dispatch(setImproveSearchQueryMode(selectedMode.key))

        persistSettings({ improveSearchQueryMode: selectedMode?.key })
    }

    const onSearchApproachChange = (event: SelectChangeEvent) => {
        const selectedApproach = JSON.parse(event.target.value)
        dispatch(setSearchApproach(selectedApproach.key))
        persistSettings({ searchApproach: selectedApproach?.key })
    }
    const onShowWelcomeMessageChange = (value: boolean) => {
        persistSettings({ showWelcomeMessage: value })
        dispatch(setShowWelcomeMessage(value))
    }

    const pdfCitationDisplayMode = useSelector((state: any) => state.chat.pdfCitationDisplayMode)

    const onPdfCitationDisplayModeChange = (event: SelectChangeEvent<DownloadType>) => {
        const newMode = event.target.value as DownloadType
        dispatch(setPdfCitationDisplayMode(newMode))
        persistSettings({ pdfCitationDisplayMode: newMode })
    }

    const onCitationsHighlightModeChange = (event: SelectChangeEvent<CitationsHighlightMode>) => {
        dispatch(setCitationsHighlightMode(event.target.value as CitationsHighlightMode))
        persistSettings({
            citationsHighlightMode: event.target.value as CitationsHighlightMode,
        })
    }

    const onQualityModeChange = (mode: QualityMode) => {
        const { tokens_usage_mode, reasoning_enabled } = qualityModeToOverrides(mode)
        dispatch(setTokensUsageMode(tokens_usage_mode))
        dispatch(setUseReasoning(reasoning_enabled))
        persistSettings({
            qualityMode: mode,
            selectedTokensUsageMode: tokens_usage_mode,
            useReasoning: reasoning_enabled,
        })
    }

    const onUseReasoningChange = (value: boolean) => {
        dispatch(setUseReasoning(value))
        persistSettings({
            useReasoning: value,
            qualityMode: overridesToQualityMode(settings.selectedTokensUsageMode, value),
        })
    }

    const onModelProviderChange = (event: SelectChangeEvent) => {
        const selectedProvider = event.target.value
        dispatch(setModelProvider(selectedProvider))
        persistSettings({ modelProvider: selectedProvider })
    }

    return {
        selectedModelState,
        selectedTokensUsageModeState,
        selectedAssistantTypeState,
        selectedUserTypeState,
        selectedSearchApproachState,
        selectedImproveSearchQueryModeState,
        selectedPreselectContextFilesModeState,
        selectedDocsPreselectSearchThreshold,
        onAnswerGenerationPromptTemplateChange,
        onCognitiveSearchQueryGenerationPromptTemplateChange,
        onTemperatureCountChange,
        onUseSuggestFollowupQuestionsChange,
        onAssistantTypeChange,
        onUserTypeChange,
        onModelChange,
        onTokensUsageModeChange,
        onSearchApproachChange,
        onShowWelcomeMessageChange,
        pdfCitationDisplayMode,
        onPdfCitationDisplayModeChange,
        onCitationsHighlightModeChange,
        onDocsSearchThresholdChange,
        onModelProviderChange,
        selectedProviderState,
        onImproveSearchQueryModeChange,
        onPreselectContextFilesModeChange,
        onDocsPreselectSearchThresholdChange,
        onUseReasoningChange,
        qualityMode: personalSettings.qualityMode,
        onQualityModeChange,
        ...settings,
    }
}
